'use client';

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import Toast, { type ToastItemData, type ToastType } from '../components/Toast';

const DEFAULT_DURATION_MS = 4500;
const EXIT_MS = 220;
const MAX_TOASTS = 5;

export type ShowToastOptions = {
    /** alert (rød), warning (gul), success (grønn) eller info (blå) */
    type?: ToastType | 'error';
    message: string;
    title?: string;
    /** Millisekunder synlig. Standard 4500. */
    duration?: number;
};

type ToastContextValue = {
    showToast: {
        (message: string, type?: ToastType | 'error'): void;
        (options: ShowToastOptions): void;
    };
};

const ToastContext = createContext<ToastContextValue | null>(null);

function normalizeType(raw?: ToastType | 'error'): ToastType {
    if (raw === 'error' || raw === 'alert') return 'alert';
    if (raw === 'warning' || raw === 'success' || raw === 'info') return raw;
    return 'info';
}

function newToastId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<ToastItemData[]>([]);
    const [leavingIds, setLeavingIds] = useState<Set<string>>(() => new Set());
    const [pausedId, setPausedId] = useState<string | null>(null);
    const timersRef = useRef<Map<string, { timeoutId: number; remaining: number; startedAt: number }>>(
        new Map(),
    );

    const removeNow = useCallback((id: string) => {
        const timer = timersRef.current.get(id);
        if (timer) {
            window.clearTimeout(timer.timeoutId);
            timersRef.current.delete(id);
        }
        setLeavingIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        setToasts((prev) => prev.filter((t) => t.id !== id));
        setPausedId((cur) => (cur === id ? null : cur));
    }, []);

    const beginLeave = useCallback(
        (id: string) => {
            const timer = timersRef.current.get(id);
            if (timer) {
                window.clearTimeout(timer.timeoutId);
                timersRef.current.delete(id);
            }
            setLeavingIds((prev) => {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
            });
            window.setTimeout(() => removeNow(id), EXIT_MS);
        },
        [removeNow],
    );

    const armTimer = useCallback(
        (id: string, remaining: number) => {
            const existing = timersRef.current.get(id);
            if (existing) window.clearTimeout(existing.timeoutId);
            const timeoutId = window.setTimeout(() => beginLeave(id), remaining);
            timersRef.current.set(id, {
                timeoutId,
                remaining,
                startedAt: Date.now(),
            });
        },
        [beginLeave],
    );

    const showToast = useCallback(
        (messageOrOptions: string | ShowToastOptions, typeArg?: ToastType | 'error') => {
            const options: ShowToastOptions =
                typeof messageOrOptions === 'string'
                    ? { message: messageOrOptions, type: typeArg }
                    : messageOrOptions;
            const message = String(options.message || '').trim();
            if (!message) return;

            const durationRaw = options.duration != null ? Number(options.duration) : DEFAULT_DURATION_MS;
            const duration =
                Number.isFinite(durationRaw) && durationRaw > 0 ? Math.min(durationRaw, 30000) : DEFAULT_DURATION_MS;
            const item: ToastItemData = {
                id: newToastId(),
                type: normalizeType(options.type),
                message,
                title: options.title?.trim() || undefined,
                duration,
            };

            setToasts((prev) => {
                const next = [item, ...prev];
                const kept = next.slice(0, MAX_TOASTS);
                next.slice(MAX_TOASTS).forEach((dropped) => {
                    const timer = timersRef.current.get(dropped.id);
                    if (timer) {
                        window.clearTimeout(timer.timeoutId);
                        timersRef.current.delete(dropped.id);
                    }
                });
                return kept;
            });
            armTimer(item.id, duration);
        },
        [armTimer],
    );

    const pauseToast = useCallback((id: string) => {
        const timer = timersRef.current.get(id);
        if (!timer) return;
        window.clearTimeout(timer.timeoutId);
        const elapsed = Date.now() - timer.startedAt;
        const remaining = Math.max(0, timer.remaining - elapsed);
        timersRef.current.set(id, { timeoutId: 0, remaining, startedAt: Date.now() });
        setPausedId(id);
    }, []);

    const resumeToast = useCallback(
        (id: string) => {
            const timer = timersRef.current.get(id);
            setPausedId((cur) => (cur === id ? null : cur));
            if (!timer) return;
            armTimer(id, timer.remaining);
        },
        [armTimer],
    );

    const value = useMemo(() => ({ showToast }), [showToast]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                className="pointer-events-none fixed right-4 top-4 z-[200] flex flex-col items-end gap-2 sm:right-6 sm:top-6"
                aria-label="Varsler"
            >
                {toasts.map((toast) => (
                    <div key={toast.id} className="pointer-events-auto">
                        <Toast
                            toast={toast}
                            leaving={leavingIds.has(toast.id)}
                            paused={pausedId === toast.id}
                            onClose={() => beginLeave(toast.id)}
                            onPause={() => pauseToast(toast.id)}
                            onResume={() => resumeToast(toast.id)}
                        />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast(): ToastContextValue {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return ctx;
}
